import React from 'react';
import { AddressTypes, ListActionSelect } from '@sweetgreen/garnish';

import { RestaurantListAction } from '@order/components';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { mapAvailableTimes } from '../../../Checkout.utils';
import { useFirstAvailableOption } from '../../../hooks/useFirstAvailableOption/useFirstAvailableOption';
import { CheckoutBlockVStack } from '../CheckoutBlockVStack';
import type { CheckoutDetailsBlockProps } from './CheckoutDetailsBlock.types';

export const OutpostOptions = React.memo(
  ({
    orderId,
    orderType,
    availableWantedTimeOptions,
    loadingAvailableWantedTimes,
    restaurant,
    selectedTime,
    setSelectedTime,
    dropoffLocation,
    setDropoffLocation,
  }: CheckoutDetailsBlockProps) => {
    const { t } = useLocalizationContext();

    const timeOptionsGroups = React.useMemo(
      () =>
        mapAvailableTimes({
          t,
          orderType,
          times: availableWantedTimeOptions,
        }),
      [t, orderType, availableWantedTimeOptions],
    );

    useFirstAvailableOption({
      timeOptionsGroups,
      selectedTime,
      setSelectedTime,
    });

    // ──────── Telemetry ─────────

    const { track } = useTelemetry();

    const onTimeChange = React.useCallback(
      (time: string) => {
        const delta =
          new Date(time).getTime() - new Date(selectedTime).getTime();

        track('checkout.time_changed', { timeChanged: delta / (60 * 1000) });

        setSelectedTime(time);
      },
      [selectedTime, setSelectedTime, track],
    );
    // ────────────────────────────

    const availableDropOffLocations = React.useMemo(() => {
      return restaurant?.availableDropOffLocations ?? [];
    }, [restaurant?.availableDropOffLocations]);

    React.useEffect(() => {
      if (!dropoffLocation && availableDropOffLocations.length > 0) {
        setDropoffLocation(availableDropOffLocations[0].id);
      }
    }, [dropoffLocation, availableDropOffLocations, setDropoffLocation]);

    if (orderType !== AddressTypes.outpost) return null;

    return (
      <CheckoutBlockVStack testID="checkout-block-outpost">
        <ListActionSelect
          testID="checkout.pickup-time"
          iconName="IconClock"
          title={t('checkout.pickup-time')}
          modalTitle={t('checkout.choose-pickup-time')}
          value={selectedTime}
          onSelect={onTimeChange}
          options={timeOptionsGroups}
          isLoading={loadingAvailableWantedTimes}
        />
        <RestaurantListAction
          orderId={orderId}
          restaurant={restaurant}
          orderType={orderType}
        />
        {(restaurant?.availableDropOffLocations ?? []).length > 1 && (
          <ListActionSelect
            iconName="IconFloor"
            testID="checkout.drop-off-location"
            title={t('checkout.drop-off-location')}
            modalTitle={t('checkout.choose-drop-off-location')}
            value={dropoffLocation ?? 'placeholder'}
            onSelect={setDropoffLocation}
            options={[
              {
                value: 'placeholder',
                label: t('checkout.choose-drop-off-location'),
                disabled: true,
              },
              ...availableDropOffLocations.map((location) => ({
                value: location.id,
                label: location.name,
              })),
            ]}
          />
        )}
      </CheckoutBlockVStack>
    );
  },
);
